import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { HookInputProps, outputType } from 'types/hooks';
import { errorMessage } from 'config/constants';
import { getLenseConfigParser } from 'parser/pricingSimulator/lenseConfigParser';
import _ from 'lodash';
import { DimensionConfigType } from 'types/tradePromoOptimization';
import { formatNumWithKMBAlt } from 'utils/helperMethods';
interface FetchLenseConfigProp extends HookInputProps {}
function useFetchOfferData({
    url,
    method,
    onComplete,
    onError,
    queryKey,
    variables = {},
    queryConfig = {},
}: FetchLenseConfigProp) {
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<null | string>(null);
    const [data, setData] = useState<any | null>(null);
    const [count, setCount] = useState(0);
    // Queries
    const {
        data: apiData,
        isFetching,
        isSuccess,
        fetchStatus,
        error: apiError,
    } = useQuery({
        queryKey,
        queryFn: async () => {
            //only set data to null if function is being called outside/without infinite scroll
            //if offset is equal to default offset, it means request is coming without infinite scroll
            if (variables?.offset === 0) {
                setData(null);
                setError(null);
            }
            setLoading(true);
            try {
                const data = await axios({
                    method,
                    url,
                    data: { ...variables },
                });
                return data;
            } catch (error) {
                onFetchError(error);
            }
        },
        ...queryConfig,
    });
    const apiLoading = isFetching || fetchStatus === 'paused';
    const apiSuccess = !apiLoading && !apiError && isSuccess;
    //error handling
    if (!apiLoading && apiError && !error) {
        if (onError) return onError(apiError);
        onFetchError(apiError);
    }
    useEffect(() => {
        //success handling
        if (apiSuccess && apiData?.data) {
            if (error) setError(null);
            if (onComplete) return onComplete(apiData?.data);
            onFetchComplete(apiData?.data);
        } else if (apiSuccess && !apiData?.data) {
            setLoading(false);
            setData(null);
        }
    }, [apiData?.data, apiSuccess]);
    //called when fetching is complete
    function onFetchComplete(payload: any) {
        try {
            if (payload && Array.isArray(payload?.offers)) {
                const parsedData = payload?.offers?.map((item: any) => {
                    return {
                        offer: item?.offer,
                        uplift: { value: formatNumWithKMBAlt(item?.uplift, 2, 2), isPositive: item?.uplift > 0 },
                        roi: { value: formatNumWithKMBAlt(item?.roi, 2, 2), isPositive: item?.roi > 0 },
                    };
                });
                //setting all required states
                setData(parsedData);
            }
            setCount(payload?.total_count ?? 0);
        } catch (error) {
            console.error(errorMessage, error);
            onFetchError(error);
        } finally {
            setLoading(false);
        }
    }
    //called when API throws an error
    function onFetchError(error: any = errorMessage) {
        setData(null);
        setCount(0);
        setLoading(false);
        setError(error);
    }
    const outputData: outputType = {
        loading,
        error,
        data,
        count,
    };
    return outputData;
}
export { useFetchOfferData };
